import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceDashboardService } from '../../../services/maintenance-dashboard.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { AssetDowntimeRequest } from '../../../models/maintenance-dashboard-request.model';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { ReplaySubject, Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../../_shared/components/filters/date-filter/date-filter.component';
import { MakeAndModelFilterComponent } from '../../../../../_shared/components/filters/make-model-filter/make-model-filter.component';
import { MakeModelFilterData } from '../../../../../_shared/models/make-model.model';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../../_shared/helpers/object.helper';
import { LegendItem, MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries } from '../../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { TranslocoPipe , TranslocoService } from '@jsverse/transloco';
import { AssetDowntimeDataset, mapAssetDowntimeToChart, roundDowntimeHours } from '../../../helpers/asset-downtime-chart.helper';

@Component({
  selector: 'app-asset-downtime-trend-modal',
  standalone: true,
  imports: [CommonModule, MultiLineChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, MakeAndModelFilterComponent, DateFilterComponent, TranslocoPipe],
  templateUrl: './asset-downtime-trend-modal.component.html',
  styleUrl: './asset-downtime-trend-modal.component.scss'
})
export class AssetDowntimeTrendModalComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private dashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filter: WidgetFilterData = { ...this.filterService.filter };

  chartData: MultiLineChartDataPoint[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  series: MultiLineChartSeries[] = [];
  legendItems: LegendItem[] = [];

  isLoading: boolean = true;
  title: string = this.transloco.translate('maintenanceDashboard.charts.assetDowntimeTrend');
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();

  makeModelFilter: MakeModelFilterData = { makeModels: [] };
  accountIdsChanged$: ReplaySubject<number[]> = new ReplaySubject<number[]>(1);

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  async ngOnInit(): Promise<void> {
    await this.loadDowntimeData();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.accountIdsChanged$.complete();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.makeModelFilter = { makeModels: [] };
    this.accountIdsChanged$.next(filter.accountIds ?? []);
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filter = {
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData;

      await this.loadDowntimeData();
    }
  }

  onMakeModelFilterChange(filter: MakeModelFilterData): void {
    this.makeModelFilter = filter;
    this.loadDowntimeData();
  }

  async loadDowntimeData(): Promise<void> {
    this.isLoading = true;

    try {
      const vehicleModelIds = this.makeModelFilter.makeModels
        ?.map(m => m.modelId)
        .filter(id => id > 0) ?? [];

      const request: AssetDowntimeRequest = {
        accountIds: this.filter.accountIds,
        timeFrom: this.filter.startDate!,
        timeTo: this.filter.endDate!,
        interval: this.filter.interval,
        vehicleModelIds: vehicleModelIds.length > 0 ? vehicleModelIds : undefined,
      };

      this.cancelSubject.next();

      const downtimeResponse = await this.dashboardService.getAssetDowntimeData(request, this.cancelSubject);
      const { series, legend, datasets } = mapAssetDowntimeToChart(downtimeResponse.data ?? []);

      this.series = series;
      this.legendItems = legend;
      this.chartData = this.processDowntimeData(datasets);
      this.yTicks = this.calculateYTicks(this.chartData);

      // Calculate total value and percentage change from last period
      this.totalValue = 0;
      this.percentageChange = 0;
      if (this.chartData.length > 0) {
        const lastPeriod = this.chartData[this.chartData.length - 1];
        this.totalValue = this.series.reduce((sum, s) => sum + (Number(lastPeriod[s.key]) || 0), 0);

        if (this.chartData.length > 1) {
          const previousPeriod = this.chartData[this.chartData.length - 2];
          const previousValue = this.series.reduce((sum, s) => sum + (Number(previousPeriod[s.key]) || 0), 0);
          if (previousValue > 0) {
            this.percentageChange = ((this.totalValue - previousValue) / previousValue) * 100;
          }
        }
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processDowntimeData(datasets: AssetDowntimeDataset[]): MultiLineChartDataPoint[] {
    if (!datasets.length) return [];

    const daysDifference = differenceInDays(this.filter.endDate!, this.filter.startDate!);

    let groupedData: any[];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }
    // For 1 month (up to 31 days): show every day (no limit for modal)
    else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.filter.startDate!, this.filter.endDate!);
    }
    // For longer periods: group by weeks
    else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.filter.startDate!, this.filter.endDate!);
    }

    return groupedData;
  }

  private calculateYTicks(chartData: MultiLineChartDataPoint[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues: number[] = [];
    chartData.forEach(point => {
      this.series.forEach(s => {
        const value = Number(point[s.key]) || 0;
        if (value > 0) allValues.push(value);
      });
    });

    if (!allValues.length) return this.defaultYTicks;

    const max = Math.max(...allValues);
    const roundedMax = Math.max(
      Math.ceil(max / 10) * 10,
      Math.ceil(max * 1.1 / 10) * 10
    );

    const tickInterval = roundedMax / 5;
    const ticks: number[] = [];
    for (let i = 0; i <= 5; i++) {
      ticks.push(Math.round(tickInterval * i));
    }

    return ticks.sort((a, b) => b - a);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // Downtime increase is negative
  }

  formatValue(): string {
    return `${roundDowntimeHours(this.totalValue).toLocaleString('default', { maximumFractionDigits: 2 })} hrs`;
  }

  // An arrow function because the chart calls it without `this`
  formatTooltipValue = (hours: number): string =>
    this.transloco.translate('driverManager.duration.hours', { count: roundDowntimeHours(hours) });

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(point => {
      return this.series.some(s => {
        const value = Number(point[s.key]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}